import { Dispatch } from 'react'
import { AnyAction } from '@reduxjs/toolkit'
import {
  addRestaurantsData,
  deletePartRestaurants,
  resetMap,
  setActiveMap,
  setMapData,
  setRestaurantsIdsBboxData,
  setRestaurantsIdsBboxStatus,
  setRestaurantsIdsCityData,
  setRestaurantsStatus,
  setSelectedData,
  setSelectedStatus,
  setViewDesktop,
} from '@widgets/Restaurant/Map/model/slice'
import { fetchRests, fetchRestGallery, fetchRestsBySlugs } from '@widgets/Restaurant/Map/model/thunks'
import { globalStore } from '@app/model/store'
import { TypeMappingRestOutput } from '@shared/api/middleware/mappingAPI/restaurant/type'
import { API } from '@shared/api'
import { TypeMapContextState } from '@widgets/Restaurant/Map/MapContext'
import { TypeMapStateDetail } from '@widgets/Restaurant/Map/model/types'
import { LIST_METRO_BY_CITY } from '@shared/consts/listMetroByCity'
import { setFilterListByType, setFilterVisibleByType } from '@features/Restaurant/RestaurantFilters/model/slice'

/**
 * Работа store карты происходит по следующему циклу (это касается как первого отображения, так дозагрузки данных):
 * 1. Получение, создание/расширение массива и отображение маркеров (markers)
 * 2. Заполнение restaurant.list пустыми элементами до соответствующей с markers.list длины массива
 * 3. Замещение пустых данных в restaurant.list полными по мере необходимости (клики на пины, переход на ресторан, показ листинга)
 */

export type TypePropsOpenMapAC = {
  selectedSlug?: string
  preloadRests?: TypeMappingRestOutput[]
  applyFilters?: any
}

export const openMapAC = (data?: TypePropsOpenMapAC) => async (dispatch: Dispatch<AnyAction>) => {
  const urlParams = new URLSearchParams(window.location.search)
  const getQueryMap = urlParams.get('map')

  if (!getQueryMap) {
    const baseUrl = window.location.origin + window.location.pathname
    const newUrl = baseUrl + '?map=true'
    history.pushState(null, null, newUrl)
  }

  // TODO: доработать
  if (data?.applyFilters) {
    // console.log('openMapAC', data)
  }
  // TODO: доработать
  if (data.selectedSlug) {
    // const response await = dispatch(fetchRestsBySlugs([data.selectedSlug]))
    // @ts-ignore
    dispatch(setRestSelectedAC({ eventFrom: 'init', restSlug: data.selectedSlug }))
  } else {
    dispatch(setViewDesktop({ grid: true, card: false }))
  }

  dispatch(setActiveMap(true))
}

export const updateMapData = (data: TypeMapStateDetail, isUser) => async (dispatch: Dispatch<AnyAction>) => {
  const isSomeData = JSON.stringify(data) === JSON.stringify(globalStore.getState().features.map.map)
  if (isSomeData) return

  dispatch(setMapData(data))

  // Удаляем часть данных если зум отдаляется (чтобы не было большого количества маркеров)
  if (data.zoom < 10) dispatch(deletePartRestaurants(5))
  else if (data.zoom < 11) dispatch(deletePartRestaurants(15))
  else if (data.zoom < 12.5) dispatch(deletePartRestaurants(30))

  // Если действие было следствием клику по карточке на десктопе (zoom = 15), то не обновляем список
  if (!isUser && data.zoom === 15) return

  dispatch(setRestaurantsIdsBboxStatus('loading'))

  // @ts-ignore
  const response = await dispatch(fetchRests())
  // @ts-ignore
  if (!response.payload) return
  const cityListStore = globalStore.getState().global.cityList
  const currentCityStore = globalStore.getState().global.currentCity
  // @ts-ignore
  const newNearestCity = cityListStore.find((city) => city.id == response.payload.data.nearestCityId)
  const metroList = LIST_METRO_BY_CITY[newNearestCity?.slug || currentCityStore.slug]
  const metroListSlugLowerCase = metroList?.map((tag) => ({ ...tag, slug: tag.slug.toLowerCase() }))
  if (metroListSlugLowerCase) {
    dispatch(setFilterListByType({ filterType: 'metro', list: metroListSlugLowerCase }))
    dispatch(setFilterVisibleByType({ filterType: 'metro', visible: true }))
  } else {
    dispatch(setFilterVisibleByType({ filterType: 'metro', visible: false }))
  }
}

export const closeMapAC = () => (dispatch: Dispatch<AnyAction>) => {
  const baseUrl = window.location.origin + window.location.pathname
  history.pushState(null, null, baseUrl)

  dispatch(setActiveMap(false))
  dispatch(resetMap())
}

type TypeSetRestSelectedAC = {
  restData?: TypeMappingRestOutput
  restSlug?: string
  eventFrom: 'init' | 'slider' | 'pin' | 'card' | 'button-close'
  mapInstance?: TypeMapContextState
}
export const setRestSelectedAC =
  ({ restData, restSlug, eventFrom, mapInstance }: TypeSetRestSelectedAC) =>
  async (dispatch: Dispatch<AnyAction>) => {
    if (eventFrom !== 'init' && !mapInstance) return
    const { origin, pathname } = window.location
    const { restaurants, selected, galleries } = globalStore.getState().features.map
    const { isMobile } = globalStore.getState().global.client.responsive

    // Если данных на карте нет, то подразумевается, что карта была открыта с выбранным рестораном (в openMapAC data.selectedSlug)
    // В таком случае будет отображен только маркер выбранного ресторана
    if (restaurants.status === 'empty' || restSlug) {
      dispatch(setViewDesktop({ grid: false, card: true }))
      API.restaurant_read({ path: restSlug }).then((rest) => {
        dispatch(addRestaurantsData([rest]))
        dispatch(setRestaurantsStatus('success'))
        dispatch(setRestaurantsIdsCityData({ city: rest.address.city.id, restId: rest.id }))
        dispatch(setRestaurantsIdsBboxData([rest.id]))
        dispatch(setSelectedData(rest))
        dispatch(setSelectedStatus('success'))
        dispatch(setMapData({ zoom: 16, center: rest.address.coordinates }))
      })
      return
    }

    if (!restData && !restSlug) {
      dispatch(setViewDesktop({ grid: true, card: false }))
      dispatch(setSelectedData(null))
      dispatch(setSelectedStatus('empty'))
      history.pushState(null, null, `${origin + pathname}?map=true`)
      return
    }

    if (selected.data && restData && restData.id === selected.data.id) return

    // Обновляем URL
    const linkHrefForCard = `${origin + pathname}?map=true&restaurant=${restData ? restData.url.slug : restSlug}`
    history.pushState(null, null, linkHrefForCard)

    // передвигаем карту только если был клик по карточке на десктопе
    if (mapInstance && ['card'].includes(eventFrom)) {
      // если десктоп, то смещаем центр вправо, так как левую часть занимает карточка ресторана (примерно 500px шириной)
      if (!isMobile) dispatch(setViewDesktop({ grid: true, card: true }))

      dispatch(setMapData({ zoom: 15, center: restData.address.coordinates }))
    }

    if (['pin'].includes(eventFrom)) dispatch(setViewDesktop({ grid: false, card: true }))

    // Для десктопа проверяем загружена ли галерея. Если нет, то подгружаем
    if (!isMobile) {
      const isLoadedGallery = restData.id in galleries.data

      // @ts-ignore
      !isLoadedGallery && dispatch(fetchRestGallery(restData.id))
    }

    dispatch(setSelectedData(restData))
    dispatch(setSelectedStatus('success'))
  }
